import type { FunctionComponent, HTMLAttributes, ReactElement, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useMedia } from '../../hooks/useMedia';
import { L10nText } from '../../services/uiTranslations/reactBindings/L10nText';
import { useModifiers } from '../../styles/hooks/useModifiers';

import styles from './Tooltip.module.css';

export enum TooltipPositions {
  Top = 'top',
  Right = 'right',
  Left = 'left',
  Bottom = 'bottom',
}

export interface TooltipProps extends HTMLAttributes<HTMLDivElement> {
  /** Dynamic values for the L10nText translation */
  attrs?: string[];
  /** If true, the tooltip will be disabled */
  disabled?: boolean;
  /** Offset from the target element
   * @default 0
   */
  displayOffset?: number;
  /** If true, the tooltip will be displayed on touch
   * @default false
   */
  displayOnTouch?: boolean;
  /** Time in milliseconds after which the tooltip will be hidden
   * @default 2000
   */
  displayTimer?: number;
  /** If true, the tooltip will be displayed on trigger */
  displayTrigger?: boolean;
  /**  Position of the tooltip relative to the target element
   * @default TooltipPositions.Top
   */
  position?: TooltipPositions;
  /** Target refference which will be used for pointer detection */
  targetRef?: RefObject<HTMLDivElement>;
  /** key for the L10nText translation */
  text: string;
  /** If true, the tooltip will be displayed with native scrolling
   * @default false
   */
  withNativeScrolling?: boolean;
  /* When true tooltip is forced to be open */
  forceOpen?: boolean;
}

/**
 * Tooltip component wraps the child component and has a rectangular hitbox over the component.
 * To get more accurate hitbox use withTooltip() in combination with forwardRef.
 */
export const Tooltip: FunctionComponent<TooltipProps> = ({
  attrs,
  children,
  disabled = false,
  displayOffset = 0,
  displayOnTouch = false,
  displayTimer = 2000,
  displayTrigger,
  position = TooltipPositions.Top,
  targetRef,
  text,
  withNativeScrolling = false,
  forceOpen,
  ...rest
}): ReactElement => {
  const timeoutRef = useRef<number | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [domReady, setDomReady] = useState(false);

  const isTouchDevice: boolean = useMedia(
    '(hover: hover) and (pointer: coarse),' +
      '(hover: none) and (pointer: coarse),' +
      '(hover: hover) and (pointer: none),' +
      '(hover: none) and (pointer: none)'
  );

  const displayTooltip = useCallback(() => {
    setIsTooltipVisible(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      setIsTooltipVisible(false);
    }, displayTimer);
  }, [displayTimer]);

  useEffect(() => {
    if (displayTrigger) {
      displayTooltip();
    }
  }, [displayTooltip, displayTrigger]);

  const handleChildClick = (): void => {
    if (displayOnTouch && isTouchDevice) {
      displayTooltip();
    }
  };

  const onPointerLeave = (): void => {
    if (!isTouchDevice) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsTooltipVisible(false);
    }
  };

  const onPointerEnter = (): void => {
    if (!isTouchDevice) {
      showTooltip();
    }
  };

  const showTooltip = (): void => {
    setIsTooltipVisible(true);
  };

  const handleBlur = (): void => {
    if (displayOnTouch && isTouchDevice) {
      setIsTooltipVisible(false);
    }
  };

  // Add/remove listeners to the targetRef element for accurate hit detection
  useEffect(() => {
    const childEl = targetRef?.current;

    if (childEl) {
      childEl.addEventListener('pointerenter', onPointerEnter);
      childEl.addEventListener('pointerleave', onPointerLeave);
      childEl.addEventListener('onblur', handleBlur);
    }

    return (): void => {
      if (!childEl) return;
      childEl?.removeEventListener('pointerenter', onPointerEnter);
      childEl?.removeEventListener('pointerleave', onPointerLeave);
      childEl?.removeEventListener('onblur', handleBlur);
    };
  }, [targetRef, domReady]);

  const calculatePosition = (
    originalTarget: number,
    dimension: number,
    padding: number,
    windowDimension: number
  ): number => {
    return Math.min(Math.max(originalTarget, padding), windowDimension - padding - dimension);
  };

  const setTooltipStyle = (offset: number, left: string, top: string): void => {
    if (!tooltipRef.current) return;
    tooltipRef.current.style.setProperty('--tooltipOffset', `${offset}px`);
    tooltipRef.current.style.setProperty('left', left);
    tooltipRef.current.style.setProperty('top', top);
  };

  const handleTooltipPosition = useCallback((): void => {
    if (anchorRef.current && tooltipRef.current && !disabled) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      tooltipRef.current.style.removeProperty('--tooltipOffset');

      const windowScrollOffset = withNativeScrolling ? window.scrollY : 0;
      const windowPadding = 2; // distance between window and tooltip

      let xPosition: number = 0;
      let yPosition: number = 0;

      switch (position) {
        case TooltipPositions.Top:
        case TooltipPositions.Bottom: {
          const originalTargetX = anchorRect.x + anchorRect.width / 2 - tooltipRect.width / 2;
          xPosition = calculatePosition(originalTargetX, tooltipRect.width, windowPadding, window.innerWidth);
          const xOffset = (xPosition - originalTargetX) * -1;

          yPosition =
            position === TooltipPositions.Top
              ? anchorRect.y - tooltipRect.height - displayOffset
              : anchorRect.y + anchorRect.height + displayOffset;

          setTooltipStyle(xOffset, `${xPosition}px`, `${yPosition + windowScrollOffset}px`);
          break;
        }

        case TooltipPositions.Left:
        case TooltipPositions.Right: {
          const originalTargetY = anchorRect.y + anchorRect.height / 2 - tooltipRect.height / 2;
          yPosition = calculatePosition(originalTargetY, tooltipRect.height, windowPadding, window.innerHeight);
          const yOffset = (yPosition - originalTargetY) * -1;

          xPosition =
            position === TooltipPositions.Left
              ? anchorRect.x - tooltipRect.width - displayOffset
              : anchorRect.x + anchorRect.width + displayOffset;

          setTooltipStyle(yOffset, `${xPosition}px`, `${yPosition + windowScrollOffset}px`);
          break;
        }
      }

      tooltipRef.current.style.setProperty('opacity', '1');
    }
  }, [disabled, displayOffset, position, withNativeScrolling]);

  useEffect(() => {
    handleTooltipPosition();
  }, [disabled, text, isTooltipVisible, forceOpen, domReady, handleTooltipPosition]);

  useEffect(() => {
    setDomReady(true);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleTooltipPosition);
    return (): void => {
      window.removeEventListener('resize', handleTooltipPosition);
    };
  }, [isTooltipVisible, disabled, handleTooltipPosition]);

  const root = document.getElementById('tooltipRoot');

  const mods = useModifiers(styles, 'tooltip', {
    left: position === TooltipPositions.Left,
    right: position === TooltipPositions.Right,
    top: position === TooltipPositions.Top,
    bottom: position === TooltipPositions.Bottom,
    hoverOnly: !displayOnTouch,
    visible: isTooltipVisible || forceOpen,
  });

  const tooltipPortal = useCallback(() => {
    const shouldNotRender = !isTooltipVisible && !forceOpen;
    if (disabled || shouldNotRender || !root || !domReady) return null;
    return createPortal(
      <div data-testid="tooltip" className={`${styles.tooltip} ${mods}`} ref={tooltipRef}>
        <L10nText tk={text} dynamicValues={attrs} />
        <span className={styles.tooltip__after} />
      </div>,
      root
    );
  }, [attrs, disabled, domReady, isTooltipVisible, forceOpen, mods, root, text]);

  return (
    <div
      className={`${styles.tooltip__trigger}`}
      // If targetRef is not provided it means that the tooltip wrapper must serve as hitbox
      onPointerEnter={!targetRef ? onPointerEnter : undefined}
      onPointerLeave={!targetRef ? onPointerLeave : undefined}
      onBlur={!targetRef ? handleBlur : undefined}
      tabIndex={-1}
      {...rest}
    >
      <div style={{ height: '100%' }} onClick={handleChildClick} ref={anchorRef}>
        {children}
      </div>
      {tooltipPortal()}
    </div>
  );
};
